import React from "react";
import { CryptoData, Currency } from "@/types/Currency.tsx";
import GenericApi from "@/api/genericApi";
import { CryptoDataQueryKey } from "@/config/constants/QueryKeys";
import { ApiResponse } from "@/types/Api";
import { useQuery } from "@tanstack/react-query";
import { isBitramp } from "@/utils/utils";

interface Props {
  currency: Currency;
  onDelete: (symbol: string) => void;
}

const CurrencyChip: React.FC<Props> = ({ currency, onDelete }) => {
  const url = `https://min-api.cryptocompare.com/data`;

  const {
    data: cryptoData,
    isLoading,
    error,
  } = useQuery<ApiResponse<CryptoData>>({
    queryFn: async () =>
      GenericApi.get<CryptoData>(
        `/pricemultifull?fsyms=${currency?.symbol}&tsyms=${currency?.symbol}`,
        url
      ),
    queryKey: [CryptoDataQueryKey, currency?.symbol],
  });

  if (isLoading) {
    return <div>Loading ...</div>;
  }

  if (error || cryptoData?.error) {
    return <div>{cryptoData?.error?.message ?? "Something went wrong"}</div>;
  }

  const getMediaLink = () => {
    if (currency?.symbol) {
      return cryptoData?.data?.RAW?.[currency?.symbol]?.[currency?.symbol]
        ?.IMAGEURL;
    }

    return null;
  };

  const mediaLink = getMediaLink();

  const flagUrls: Record<string, string> = {
    EUR: "https://flagcdn.com/w320/eu.png",
    USD: "https://flagcdn.com/w320/us.png",
    RON: "https://flagcdn.com/w320/ro.png",
    GBP: "https://flagcdn.com/w320/gb.png",
    JPY: "https://flagcdn.com/w320/jp.png",
    PLN: "https://flagcdn.com/w320/pl.png",
    AUD: "https://flagcdn.com/w320/au.png",
    CAD: "https://flagcdn.com/w320/ca.png",
    CHF: "https://flagcdn.com/w320/ch.png",
    // Adaugă aici alte monede relevante
  };

  const imageUrl =
    (currency?.symbol && flagUrls[currency.symbol]) ||
    (mediaLink
      ? `https://www.cryptocompare.com/${mediaLink}`
      : "https://via.placeholder.com/24");

  return (
    <div className={`flex gap-2 my-2 Container justify-center items-center min-w-[150px] max-w-[200px] h-11" ${isBitramp() ? "!bg-[#464646] text-[#9F9F9F]" : ""}`}>
      <div className=" flex items-center space-x-2 p-2">
        <img
          className="w-6 h-6 object-cover rounded-full"
          src={imageUrl}
          alt="Crypto"
        />
        <span className={`${isBitramp() ? "text-[#9F9F9F]" : "text-[#222b45]"}  text-[17px] font-bold font-['Satoshi'] leading-[30px]`}>
          {currency?.symbol}
        </span>
        <span className={`capitalize text-center  text-[17px] font-normal font-['Satoshi'] ${isBitramp() ? "text-[#9F9F9F]" : "text-[#cfcfcf]"}`}>  
          {currency?.type}
        </span>
      </div>

      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        onClick={() => {
          if (currency?.symbol) {
            onDelete(currency.symbol);
            return;
          }

          //TODO ADD A FALLBACK
        }}
        className="cursor-pointer mr-2"
      >
        <path
          d="M10.6469 8.89019C10.888 9.13136 11.0114 9.42225 10.9942 9.76115C10.9657 10.3115 10.6646 10.6847 10.1767 10.895C9.70308 11.0996 9.26204 11.0007 8.89641 10.6378C7.92349 9.67256 6.95742 8.70046 5.98792 7.73121C5.88052 7.62377 5.77769 7.51518 5.60972 7.4969C5.38806 7.47289 5.20353 7.5209 5.04242 7.68835C4.68536 8.05753 4.31744 8.417 3.9541 8.78047C3.34167 9.39311 2.7298 10.0069 2.11623 10.6184C1.88657 10.847 1.6192 10.983 1.2827 10.9876C0.927354 10.9927 0.627993 10.8596 0.388047 10.623C0.153814 10.3921 -0.0112915 10.1652 0.0064188 9.75257C0.0012771 9.41996 0.10811 9.14108 0.332631 8.91648C1.1593 8.08782 1.9894 7.26201 2.82064 6.43792C2.97546 6.28476 3.14057 6.14189 3.30167 5.99445C3.64731 5.67841 3.61646 5.29551 3.2931 4.98233C2.33046 4.04909 1.37754 3.10499 0.423467 2.1626C0.329774 2.07001 0.236652 1.97 0.171524 1.85685C-0.200392 1.20935 0.0464098 0.452694 0.777102 0.106941C1.0976 -0.044504 1.39411 -0.0227873 1.69232 0.163519C2.07224 0.400116 2.42244 0.671003 2.7378 0.989324C3.5022 1.75855 4.2706 2.52321 5.03671 3.29015C5.1544 3.40788 5.2818 3.49246 5.45833 3.49931C5.652 3.50674 5.80568 3.44731 5.94165 3.30615C6.22558 3.01126 6.51866 2.72552 6.80831 2.43634C7.49501 1.74941 8.18286 1.06476 8.8667 0.374971C9.13007 0.109227 9.44257 -0.0193584 9.81392 0.00235831C9.91618 0.00864471 10.0219 0.026361 10.1178 0.0617934C10.7611 0.300105 11.2844 1.08648 10.8268 1.86599C10.7697 1.96257 10.692 2.04887 10.6126 2.12888C9.65224 3.09356 8.69246 4.05938 7.72524 5.01662C7.50929 5.23036 7.4396 5.45896 7.56128 5.73327C7.60184 5.82471 7.67326 5.90701 7.74467 5.97902C8.71074 6.95112 9.67852 7.92094 10.6469 8.89019Z"
          fill={`${isBitramp() ? "#fff" : "#202A43"}`}
          fill-opacity="0.3"
        />
      </svg>
    </div>
  );
};

export default CurrencyChip;
